import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock,
  Mail,
  RefreshCw,
  UserPlus,
  Loader2,
  Coins,
  Gift
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { AiAvatar, KIOSK_MESSAGES } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_TIMEOUT = 120;

const categoryNames = {
  disabled_blue_badge: "Disabled Blue Badge",
  adult_with_child: "Adult with Child in Vehicle",
  extra_wide: "Extra Wide Bay",
  nhs_staff: "NHS Staff",
  military: "Military / Ex-Military"
};

export default function KioskStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lookupResult, vehicleReg } = location.state || {};

  const [showEmailUpdate, setShowEmailUpdate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [updating, setUpdating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const idleTimerRef = useRef(null);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (vehicleReg) {
      fetchCreditBalance();
    }
  }, [vehicleReg]);

  const fetchCreditBalance = async () => {
    if (!vehicleReg) return;
    
    setLoadingCredits(true);
    try {
      const response = await axios.get(`${API}/credit-ledger/balance/${vehicleReg}`);
      setCreditBalance(response.data);
    } catch (err) {
      console.error("Failed to fetch credit balance:", err);
      // Silently fail - credits are not critical
    } finally {
      setLoadingCredits(false);
    }
  };

  if (!lookupResult) {
    navigate("/kiosk/lookup");
    return null;
  }

  const { found, registration, message, can_update_email, can_confirm_ownership, is_expired } = lookupResult;

  const handleUpdateEmail = async () => {
    if (!newEmail.trim() || !registration) return;

    setUpdating(true);
    try {
      await axios.patch(`${API}/registrations/${registration.registration_id}/contact`, {
        email: newEmail
      });
      toast.success("Email updated successfully");
      setShowEmailUpdate(false);
      // Update local state
      registration.email = newEmail;
    } catch (err) {
      console.error("Email update error:", err);
      toast.error("Failed to update email");
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmOwnership = async () => {
    if (!registration) return;

    setConfirming(true);
    try {
      await axios.post(`${API}/registrations/${registration.registration_id}/confirm-ownership`, {
        confirmed: true
      });
      toast.success("Ownership confirmed");
      setOwnershipConfirmed(true);
    } catch (err) {
      console.error("Ownership confirmation error:", err);
      toast.error("Failed to confirm ownership");
    } finally {
      setConfirming(false);
    }
  };

  const getStatusIcon = () => {
    if (!found) {
      return <XCircle className="w-16 h-16 text-slate-400" />;
    }
    if (is_expired || registration?.status === "expired") {
      return <Clock className="w-16 h-16 text-amber-500" />;
    }
    if (registration?.status === "suspended") {
      return <AlertTriangle className="w-16 h-16 text-orange-500" />;
    }
    if (registration?.status === "revoked") {
      return <XCircle className="w-16 h-16 text-red-500" />;
    }
    return <CheckCircle className="w-16 h-16 text-green-500" />;
  };

  const getStatusColor = () => {
    if (!found) return "bg-slate-100";
    if (is_expired || registration?.status === "expired") return "bg-amber-50";
    if (registration?.status === "suspended") return "bg-orange-50";
    if (registration?.status === "revoked") return "bg-red-50";
    return "bg-green-50";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Indefinite";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-status">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <ParkWiseLogo size="lg" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto w-full">
        {/* AI Avatar with dynamic message */}
        <div className="mb-4">
          <AiAvatar 
            message={
              !found ? KIOSK_MESSAGES.status_not_found :
              is_expired ? `Your registration for ${categoryNames[registration?.category] || 'designated parking'} has expired. Please tap Register Now to renew your parking entitlement.` :
              `You are checking your registration for ${categoryNames[registration?.category] || 'designated parking'}. Your vehicle registration is active. You are authorised to use designated parking spaces.`
            } 
            size="small"
            autoSpeak={true}
          />
        </div>

        {/* Vehicle Registration Display */}
        <div className="text-center mb-4">
          <p className="text-sm uppercase tracking-wider text-slate-500 mb-2">Vehicle Registration</p>
          <p className="text-4xl font-bold font-mono tracking-wider text-slate-900">
            {vehicleReg}
          </p>
        </div>

        {/* Status Card */}
        <div className={`w-full rounded-2xl ${getStatusColor()} p-8 mb-6`}>
          <div className="flex flex-col items-center text-center">
            {getStatusIcon()}
            
            <p className="mt-4 text-xl text-slate-800 font-medium max-w-md">
              {message}
            </p>

            {found && registration && (
              <div className="mt-6 w-full max-w-md">
                <div className="bg-white rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Category</span>
                    <span className="font-medium text-slate-900">
                      {categoryNames[registration.category] || registration.category}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className={`font-medium capitalize ${
                      registration.status === 'active' && !is_expired ? 'text-green-600' : 
                      is_expired ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {is_expired ? 'Expired' : registration.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Valid Until</span>
                    <span className="font-medium text-slate-900">
                      {formatDate(registration.expires_at)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email</span>
                    <span className="font-medium text-slate-900">
                      {registration.email}
                    </span>
                  </div>
                </div>

                {/* Parking Credits Section */}
                {creditBalance && creditBalance.total_balance > 0 && (
                  <div className="mt-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200" data-testid="credit-balance-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Coins className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-800">Parking Credits Available</span>
                      </div>
                      <span className="text-2xl font-bold text-amber-600" data-testid="credit-balance">
                        {creditBalance.total_balance}
                      </span>
                    </div>
                    
                    {creditBalance.recent_credits && creditBalance.recent_credits.length > 0 && (
                      <div className="border-t border-amber-200 pt-3 mt-3">
                        <p className="text-xs text-amber-700 mb-2">Recent credits earned:</p>
                        <div className="space-y-1">
                          {creditBalance.recent_credits.slice(0, 3).map((credit, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-amber-700">{credit.retailer_name}</span>
                              <span className="text-amber-600 font-medium">+{credit.credit_value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-amber-600 mt-3">
                      Credits earned from local visits
                    </p>
                  </div>
                )}

                {loadingCredits && (
                  <div className="mt-4 flex items-center justify-center text-slate-400 py-2">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm">Loading credits...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions for valid registrations */}
        {found && can_update_email && !showEmailUpdate && (
          <div className="w-full space-y-3">
            {/* Update Email Button */}
            <button
              onClick={() => setShowEmailUpdate(true)}
              className="w-full bg-white border-2 border-slate-200 rounded-xl p-4 flex items-center justify-center gap-3 hover:border-blue-400 transition-colors"
              data-testid="update-email-button"
            >
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-slate-700">Update Email Address</span>
            </button>

            {/* Confirm Ownership Button */}
            {can_confirm_ownership && !ownershipConfirmed && (
              <button
                onClick={handleConfirmOwnership}
                disabled={confirming}
                className="w-full bg-white border-2 border-slate-200 rounded-xl p-4 flex items-center justify-center gap-3 hover:border-green-400 transition-colors"
                data-testid="confirm-ownership-button"
              >
                {confirming ? (
                  <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                ) : (
                  <RefreshCw className="w-5 h-5 text-green-600" />
                )}
                <span className="font-medium text-slate-700">
                  {confirming ? "Confirming..." : "Confirm Vehicle Ownership"}
                </span>
              </button>
            )}

            {ownershipConfirmed && (
              <div className="w-full bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center justify-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-700">Ownership Confirmed</span>
              </div>
            )}
          </div>
        )}

        {/* Email Update Form */}
        {showEmailUpdate && (
          <div className="w-full bg-white rounded-xl border-2 border-slate-200 p-6 space-y-4">
            <Label htmlFor="new-email" className="text-lg font-medium text-slate-700">
              New Email Address
            </Label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="your@email.com"
              className="h-14 text-xl"
              data-testid="input-new-email"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailUpdate(false)}
                className="flex-1 kiosk-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEmail}
                disabled={updating || !newEmail.trim()}
                className="flex-1 kiosk-btn-primary"
                data-testid="save-email-button"
              >
                {updating ? "Saving..." : "Save Email"}
              </button>
            </div>
          </div>
        )}

        {/* Not found - offer registration */}
        {!found && (
          <button
            onClick={() => navigate("/kiosk/category")}
            className="kiosk-btn-primary flex items-center gap-3"
            data-testid="register-button"
          >
            <UserPlus className="w-6 h-6" />
            <span>Register Now</span>
          </button>
        )}

        {/* Expired - offer re-registration */}
        {found && is_expired && (
          <button
            onClick={() => navigate("/kiosk/category")}
            className="kiosk-btn-primary flex items-center gap-3 mt-4"
            data-testid="re-register-button"
          >
            <RefreshCw className="w-6 h-6" />
            <span>Re-Register</span>
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-slate-200">
        <button
          onClick={() => navigate("/kiosk/lookup")}
          className="kiosk-btn-secondary flex items-center gap-3"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
          <span>Check Another</span>
        </button>

        <button
          onClick={() => navigate("/kiosk")}
          className="kiosk-btn-primary"
          data-testid="done-button"
        >
          Done
        </button>
      </div>
    </div>
  );
}
